import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useMemo, useState } from "react"
import { useFieldArray } from "react-hook-form"
import * as yup from "yup"

import { gql } from "@apollo/client"
import TextInput from "@components/text_input"
import {
  channelsGetAllPlatform,
  createRandomMessageMutation
} from "@domain/ai_answers"
import useControlledForm from "@hooks/use_controlled_form"
import { useCustomersQuery } from "@hooks/use_customers"
import useTranslate from "@hooks/use_translate"
import AddIcon from "@mui/icons-material/Add"
import BoltIcon from "@mui/icons-material/Bolt"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import PersonRemoveIcon from "@mui/icons-material/PersonRemove"
import RestartAltIcon from "@mui/icons-material/RestartAlt"
import ScienceIcon from "@mui/icons-material/Science"
import SendIcon from "@mui/icons-material/Send"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { mutate, query } from "@utils/graphql"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import {
  BooleanResponse,
  CreateRandomMessageInput,
  CustomerPersonsListResponse,
  MessageType,
  OTagsAllResponse,
  PlatformType,
  QueryCustomerPersonsArgs,
  TagType,
  TicketPriority,
  TicketStateListResponse,
  TicketStatusType,
  TicketTypeListResponse
} from "__generated__/graphql"
import customerPersonsQuery from "__generated__/queries/queries/customerPersons"

type MessageMediaFormItem = {
  path: string
  type: string
  name?: string
}

const DEFAULT_MEDIA_ITEM: MessageMediaFormItem = {
  path: "",
  type: "image/jpeg",
  name: ""
}

const SOCIAL_PLATFORMS = [
  PlatformType.Instagram,
  PlatformType.Facebook,
  PlatformType.Tiktok
]

const QUESTION_PLATFORMS = [
  PlatformType.Trendyol,
  PlatformType.Pazarama,
  PlatformType.Hepsiburada,
  PlatformType.Ebay,
  PlatformType.N11
]

// Instagram/Facebook/TikTok can deliver either a post comment or a plain DM.
// Anything else has a single inbound shape, so the selector stays hidden there.
const SOCIAL_MESSAGE_TYPES = [MessageType.Comment, MessageType.TextMessage]

function isSocialPlatform(platformType?: PlatformType): boolean {
  return !!platformType && SOCIAL_PLATFORMS.includes(platformType)
}

function isQuestionPlatform(platformType?: PlatformType): boolean {
  return !!platformType && QUESTION_PLATFORMS.includes(platformType)
}

export type MessageTestFormData = {
  customerId: string
  apiUrl: string
  // Optional because bulk mode drives the channel from the platform picker.
  dbDriverId?: string
  messages: string
  platformType?: PlatformType
  messageType?: MessageType
  channelId?: string
  channelPersonId?: string
  name?: string
  phone?: string
  delayInSeconds?: number
  postDescription?: string
  postImageUrl?: string
  postUrl?: string
  productImageUrl?: string
  productLink?: string
  productMainId?: string
  productName?: string
  sku?: string
  subject?: string
  isTicket?: boolean
  ticketType?: string
  ticketStateId?: string
  ticketAssigneeId?: string
  ticketPriority?: TicketPriority
  tagIds?: string
  mediaUrl?: string
  mediaType?: string
  medias?: MessageMediaFormItem[]
  bulkEnabled?: boolean
  bulkPlatformTypes?: PlatformType[]
  bulkConversationCount?: number
  bulkMessagesPerConversation?: number
  bulkDelayMs?: number
  bulkContactPrefix?: string
}

// Which of the two send buttons is currently in-flight.
type SendMode = "keepContact" | "freshContact"

/** Mirrors RANDOM_MESSAGE_BULK_LIMITS in api-v2 Message/core/randomMessage.constants. */
const BULK_LIMITS = {
  MAX_PLATFORMS: 3,
  MAX_CONVERSATIONS: 200,
  MAX_MESSAGES_PER_CONVERSATION: 50,
  MAX_TOTAL_MESSAGES: 2000,
  MIN_DELAY_MS: 50
}

const platformIcons: Record<string, string> = {
  [PlatformType.WhatsAppWeb]: "💬",
  [PlatformType.WhatsAppBusiness]: "✅",
  [PlatformType.MetaWhatsAppBusiness]: "🟢",
  [PlatformType.Instagram]: "📷",
  [PlatformType.Facebook]: "👥",
  [PlatformType.Telegram]: "✈️",
  [PlatformType.Email]: "📧",
  [PlatformType.LiveChat]: "💭",
  [PlatformType.Tiktok]: "🎵",
  [PlatformType.Phone]: "📞",
  [PlatformType.Amazon]: "📦",
  [PlatformType.Ebay]: "🛒",
  [PlatformType.Hepsiburada]: "🛍️",
  [PlatformType.N11]: "🏪",
  [PlatformType.Trendyol]: "🛍️",
  [PlatformType.Pazarama]: "🛍️",
  [PlatformType.Survey]: "📊"
}

const API_URLS = [
  { label: "Local (localhost:3005)", value: "http://localhost:3005" },
  { label: "Dev (apidev.qpien.com)", value: "https://apidev.qpien.com" },
  { label: "Alfa (apialfa.qpien.com)", value: "https://apialfa.qpien.com" },
  { label: "Gamma (apigamma.qpien.com)", value: "https://apigamma.qpien.com" },
  { label: "Bulk (apibulk.qpien.com)", value: "https://apibulk.qpien.com" }
]

type MessageTemplate = {
  id: string
  name: string
  icon: string
  data: Partial<MessageTestFormData>
  isCustom?: boolean
}

type RandomMessageBulkPayload = {
  targets: {
    platformType: PlatformType
    dbDriverId: string
    channelId?: string
  }[]
  conversationCount: number
  messagesPerConversation: number
  delayMs?: number
  contactPrefix?: string
  messages?: string[]
}

// `bulk` is typed locally until admin-panel-v2 codegen is re-run against the new
// schema (see the PR note) — the generated CreateRandomMessageInput predates it.
type RandomMessagePayload = CreateRandomMessageInput & {
  bulk?: RandomMessageBulkPayload
  messageType?: MessageType
  subject?: string
  ticketAssigneeId?: string
  ticketStateId?: string
  ticketPriority?: TicketPriority
  ticketType?: string
  tagIds?: string[]
}

function buildMediaItems(data: MessageTestFormData): MessageMediaFormItem[] {
  const explicitMedias =
    data.medias
      ?.map((item) => ({
        path: item?.path?.trim() || "",
        type: item?.type?.trim() || "image/jpeg",
        name: item?.name?.trim() || ""
      }))
      .filter((item) => item.path.length > 0) || []

  if (explicitMedias.length > 0) {
    return explicitMedias
  }

  if (data.mediaUrl?.trim()) {
    return [
      {
        path: data.mediaUrl.trim(),
        type: data.mediaType?.trim() || "image/jpeg",
        name: data.mediaUrl.split("/").pop() || "media"
      }
    ]
  }

  return [DEFAULT_MEDIA_ITEM]
}

const GET_TICKET_TYPES = gql`
  query getTicketTypesForMessageTest($input: GetTicketTypesInput) {
    getTicketTypes(input: $input) {
      success
      data {
        _id
        name
      }
    }
  }
`

const GET_TICKET_STATES = gql`
  query getTicketStatesForMessageTest($input: GetTicketStatesInput) {
    getTicketStates(input: $input) {
      success
      data {
        _id
        name
        category
      }
    }
  }
`

const GET_TICKET_TAGS = gql`
  query getTicketTagsForMessageTest($input: GetTagsInput) {
    getTagsList(input: $input) {
      success
      data {
        docs {
          _id
          name
        }
      }
    }
  }
`

const DELETE_RANDOM_MESSAGE_TEST_CONTACT = gql`
  mutation deleteRandomMessageTestContact(
    $data: DeleteRandomMessageTestContactInput!
  ) {
    deleteRandomMessageTestContact(data: $data) {
      success
      data
      code
      message
    }
  }
`

const DEFAULT_TEMPLATES: MessageTemplate[] = [
  {
    id: "instagram-comment",
    name: "Instagram Comment Test",
    icon: "📷",
    data: {
      customerId: "6a604c828be5a11684412fc3",
      dbDriverId: "68551c9cb14634ac9628168a",
      messages: "Bu harika bir gönderi! 🎉\nÇok beğendim 👍",
      platformType: PlatformType.Instagram,
      messageType: MessageType.Comment,
      channelId: "17841448601954795",
      channelPersonId: "6a604c828be5a11684412fc3",
      name: "AI Test",
      phone: "AI Test",
      delayInSeconds: 13000,
      postDescription: "5 numaralı ürünümüz şubemizde",
      postImageUrl:
        "https://qpien-dev.s3.us-east-2.amazonaws.com/urlInfo/instagram/17870534450718480-6a604c828be5a11684412fc3",
      postUrl:
        "https://www.instagram.com/reel/Ch9_dkxD54F/?utm_source=ig_web_copy_link"
    }
  },
  {
    id: "instagram-direct-message",
    name: "Instagram DM Test",
    icon: "📷",
    data: {
      customerId: "6a604c828be5a11684412fc3",
      dbDriverId: "68551c9cb14634ac9628168a",
      messages: "Merhaba 👋\nBisiklet fiyatları ne kadar?",
      platformType: PlatformType.Instagram,
      messageType: MessageType.TextMessage,
      channelId: "17841448601954795",
      channelPersonId: "6a604c828be5a11684412fc3",
      name: "AI Test",
      phone: "AI Test",
      delayInSeconds: 13000
    }
  },
  {
    id: "hepsiburada-question",
    name: "Hepsiburada Question Test",
    icon: "🛍️",
    data: {
      customerId: "6a604c828be5a11684412fc3",
      dbDriverId: "68d542f282773a7d42db0d94",
      messages: "Bu ürün ne zaman gelir?\nKargo ücreti var mı?",
      platformType: PlatformType.Hepsiburada,
      channelId: "955768",
      name: "AI Test",
      phone: "AI Test",
      channelPersonId: "6a604c828be5a11684412fc3",
      delayInSeconds: 13000,
      productImageUrl:
        "https://productimages.hepsiburada.net/s/777/1100/110000711893880.jpg",
      productLink: "https://hepsiburada.com/a-p-HBCV00006JH95Q",
      productMainId: "476311718",
      productName:
        "Porlistan Amara Çift Kapak Emaye Çaydanlık Takımı, Desenli Emaye Çaydanlık Seti",
      sku: "HBCV00006JH95Q"
    }
  },
  {
    id: "whatsapp-message",
    name: "WhatsApp Message Test",
    icon: "💬",
    data: {
      customerId: "6a604c828be5a11684412fc3",
      dbDriverId: "6853db33233e8e320457dc35",
      channelPersonId: "905012345678",
      messages: "Merhaba! 👋\nHangi kargo ile çalışıyorsunuz?",
      platformType: PlatformType.WhatsAppWeb,
      channelId: "17841448601954795",
      name: "AI Test",
      phone: "905551234567",
      delayInSeconds: 5000
    }
  },
  {
    id: "email-ticket",
    name: "Email Ticket Test",
    icon: "🎫",
    data: {
      customerId: "6a604c828be5a11684412fc3",
      dbDriverId: "6853db33233e8e320457dc35",
      messages:
        "Merhaba, siparişim hala gelmedi.\nKargo takibi için destek olur musunuz?",
      platformType: PlatformType.Email,
      channelId: "support@qpien.test",
      channelPersonId: "customer.ticket.test@qpien.test",
      name: "Ticket Test User",
      phone: "customer.ticket.test@qpien.test",
      subject: "Sipariş teslimatı hakkında destek talebi",
      delayInSeconds: 5000,
      isTicket: true,
      ticketPriority: TicketPriority.Normal
    }
  }
]

const STORAGE_KEY = "message-test-templates"

function defaultFormValues(): Partial<MessageTestFormData> {
  return {
    apiUrl: process.env.REACT_APP_API_URL || "https://apidev.qpien.com",
    delayInSeconds: 13000,
    name: "AI Test",
    phone: "AI Test",
    messages: "",
    channelId: "17841448601954795",
    channelPersonId: "6a604c828be5a11684412fc3",
    messageType: MessageType.Comment,
    isTicket: false,
    ticketPriority: TicketPriority.Normal,
    medias: [{ ...DEFAULT_MEDIA_ITEM }],
    bulkEnabled: false,
    bulkPlatformTypes: [],
    bulkConversationCount: 10,
    bulkMessagesPerConversation: 5,
    bulkDelayMs: 300,
    bulkContactPrefix: "qpien-load"
  }
}

function Section(props: {
  title: string
  hint?: string
  action?: React.ReactNode
  highlighted?: boolean
  children: React.ReactNode
}) {
  const { title, hint, action, highlighted, children } = props
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, ...(highlighted && { borderColor: "primary.main" }) }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {title}
        </Typography>
        {action}
      </Stack>
      {hint && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.25 }}
        >
          {hint}
        </Typography>
      )}
      <Stack gap={1.5} mt={1.25}>
        {children}
      </Stack>
    </Paper>
  )
}

export default function MessageTest() {
  const { translate } = useTranslate()
  const schema = yup
    .object({
      customerId: yup.string().required("Customer ID is required"),
      apiUrl: yup.string().required("API URL is required"),
      // Bulk mode drives everything from the platform picker below, so the
      // single-message channel fields must stop being required there.
      bulkEnabled: yup.boolean(),
      bulkPlatformTypes: yup
        .array()
        .of(
          yup
            .mixed<PlatformType>()
            .oneOf(Object.values(PlatformType))
            .required()
        )
        .when("bulkEnabled", {
          is: true,
          then: (field) =>
            field
              .min(1, "Select at least one platform")
              .max(
                BULK_LIMITS.MAX_PLATFORMS,
                `Select at most ${BULK_LIMITS.MAX_PLATFORMS} platforms`
              )
              .required("Select at least one platform"),
          otherwise: (field) => field.notRequired()
        }),
      bulkConversationCount: yup.number().when("bulkEnabled", {
        is: true,
        then: (field) =>
          field
            .typeError("Enter a conversation count")
            .min(1, "At least 1 conversation")
            .max(
              BULK_LIMITS.MAX_CONVERSATIONS,
              `At most ${BULK_LIMITS.MAX_CONVERSATIONS} conversations`
            )
            .required("Conversation count is required"),
        otherwise: (field) => field.notRequired()
      }),
      bulkMessagesPerConversation: yup.number().when("bulkEnabled", {
        is: true,
        then: (field) =>
          field
            .typeError("Enter a message count")
            .min(1, "At least 1 message")
            .max(
              BULK_LIMITS.MAX_MESSAGES_PER_CONVERSATION,
              `At most ${BULK_LIMITS.MAX_MESSAGES_PER_CONVERSATION} messages per conversation`
            )
            .required("Message count is required"),
        otherwise: (field) => field.notRequired()
      }),
      bulkDelayMs: yup
        .number()
        .transform((value: unknown, original: unknown) => {
          if (original === "") return undefined

          return typeof value === "number" ? value : undefined
        })
        .min(BULK_LIMITS.MIN_DELAY_MS, `Minimum ${BULK_LIMITS.MIN_DELAY_MS}ms`),
      bulkContactPrefix: yup.string(),
      dbDriverId: yup.string().when("bulkEnabled", {
        is: true,
        then: (field) => field.notRequired(),
        otherwise: (field) => field.required("DB Driver ID is required")
      }),
      messages: yup
        .string()
        .defined()
        .test(
          "message-or-media",
          "At least one message or media is required",
          function (this: yup.TestContext, value) {
            if (this.parent?.bulkEnabled) return true

            const mediaUrl = this.parent?.mediaUrl as string | undefined
            const medias = this.parent?.medias as
              | MessageMediaFormItem[]
              | undefined
            const hasMedia =
              Boolean(mediaUrl?.trim()) ||
              Boolean(medias?.some((media) => media?.path?.trim()))
            return Boolean(value?.trim()) || hasMedia
          }
        ),
      platformType: yup
        .mixed<PlatformType>()
        .oneOf(Object.values(PlatformType))
        .when("bulkEnabled", {
          is: true,
          then: (field) => field.notRequired(),
          otherwise: (field) => field.required("Platform Type is required")
        }),
      messageType: yup.mixed<MessageType>().oneOf(SOCIAL_MESSAGE_TYPES),
      channelId: yup.string().when("bulkEnabled", {
        is: true,
        then: (field) => field.notRequired(),
        otherwise: (field) => field.required("Channel ID is required")
      }),
      channelPersonId: yup.string(),
      name: yup.string(),
      phone: yup.string(),
      delayInSeconds: yup.number().min(0, "Delay must be positive"),
      postDescription: yup.string(),
      postImageUrl: yup.string(),
      postUrl: yup.string(),
      productImageUrl: yup.string(),
      productLink: yup.string(),
      productMainId: yup.string(),
      productName: yup.string(),
      sku: yup.string(),
      subject: yup.string(),
      isTicket: yup.boolean(),
      ticketType: yup.string(),
      ticketStateId: yup.string(),
      ticketAssigneeId: yup.string(),
      ticketPriority: yup
        .mixed<TicketPriority>()
        .oneOf(Object.values(TicketPriority) as TicketPriority[]),
      tagIds: yup.string(),
      mediaUrl: yup.string(),
      mediaType: yup.string(),
      medias: yup.array().of(
        yup.object({
          path: yup.string().default(""),
          type: yup.string().default("image/jpeg"),
          name: yup.string().default("")
        })
      )
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isValid }
  } = useControlledForm<MessageTestFormData>({
    resolver: yupResolver<MessageTestFormData>(schema),
    mode: "all",
    defaultValues: defaultFormValues()
  })
  const {
    fields: mediaFields,
    append: appendMedia,
    remove: removeMedia,
    replace: replaceMedia
  } = useFieldArray({
    control,
    name: "medias"
  })
  const customerId = watch("customerId")
  const dbDriverId = watch("dbDriverId")
  const platformType = watch("platformType")
  const messageType = watch("messageType")
  const isTicketEnabled = watch("isTicket")
  const apiUrl = watch("apiUrl")
  const channelId = watch("channelId")
  const channelPersonId = watch("channelPersonId")

  const bulkEnabled = watch("bulkEnabled")
  const bulkPlatformTypes = watch("bulkPlatformTypes")
  const bulkConversationCount = watch("bulkConversationCount")
  const bulkMessagesPerConversation = watch("bulkMessagesPerConversation")

  const isSocial = isSocialPlatform(platformType)
  const isCommentSelected =
    isSocial && (messageType || MessageType.Comment) === MessageType.Comment

  const [sendMode, setSendMode] = useState<SendMode | null>(null)
  const loading = sendMode !== null
  const [lastResult, setLastResult] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [customTemplates, setCustomTemplates] = useState<MessageTemplate[]>([])
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false)
  const [templateName, setTemplateName] = useState("")

  // The delete mutation needs the full contact identity; bulk mode has no single
  // contact to reset, so the delete-and-send button is meaningless there.
  const canDeleteContact = Boolean(
    !bulkEnabled && customerId && channelId && channelPersonId
  )

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const templates = JSON.parse(stored) as MessageTemplate[]
        setCustomTemplates(templates)
      } catch (error) {
        console.error("Failed to load templates:", error)
      }
    }
  }, [])

  const allTemplates = [...DEFAULT_TEMPLATES, ...customTemplates]

  function applyTemplate(template: MessageTemplate) {
    const nextMedias = buildMediaItems(template.data as MessageTestFormData)
    reset({
      ...(template.data as MessageTestFormData),
      medias: nextMedias
    })
    replaceMedia(nextMedias)
    showSuccessToast(`Template "${template.name}" applied!`)
  }

  function saveAsTemplate() {
    if (!templateName.trim()) {
      showErrorToast("Please enter a template name")
      return
    }

    if (!isValid) {
      showErrorToast("Please fill all required fields before saving template")
      return
    }

    const currentData = watch()
    const newTemplate: MessageTemplate = {
      id: `custom-${Date.now()}`,
      name: templateName.trim(),
      icon: platformIcons[currentData.platformType || ""] || "📝",
      data: {
        ...currentData,
        medias: buildMediaItems(currentData)
      },
      isCustom: true
    }

    const updatedTemplates = [...customTemplates, newTemplate]
    setCustomTemplates(updatedTemplates)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTemplates))

    showSuccessToast(`Template "${templateName}" saved!`)
    setTemplateName("")
    setShowSaveTemplateDialog(false)
  }

  function deleteTemplate(templateId: string) {
    const updatedTemplates = customTemplates.filter((t) => t.id !== templateId)
    setCustomTemplates(updatedTemplates)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedTemplates))
    showSuccessToast("Template deleted!")
  }

  function addMediaRow() {
    appendMedia({ ...DEFAULT_MEDIA_ITEM })
  }

  function deleteMediaRow(index: number) {
    if (mediaFields.length === 1) {
      replaceMedia([{ ...DEFAULT_MEDIA_ITEM }])
      return
    }

    removeMedia(index)
  }

  async function deleteExistingContactIfAny(
    data: MessageTestFormData
  ): Promise<boolean> {
    if (!data.customerId || !data.channelId || !data.channelPersonId) {
      return false
    }

    const context = data.apiUrl ? { uri: `${data.apiUrl}/graphql` } : undefined

    const deleteResponse = await mutate<{
      deleteRandomMessageTestContact?: BooleanResponse
    }>({
      mutation: DELETE_RANDOM_MESSAGE_TEST_CONTACT,
      variables: {
        data: {
          customerId: data.customerId,
          channelId: data.channelId,
          channelPersonId: data.channelPersonId,
          platformType: data.platformType
        }
      },
      context
    })

    if (!deleteResponse.isSuccess) {
      showErrorToast(
        String(
          deleteResponse.errorMessage || "Failed to delete existing contact"
        )
      )
      return false
    }

    return !!deleteResponse.data?.deleteRandomMessageTestContact?.data
  }

  /**
   * Bulk mode: N conversations x M messages, spread over the selected platforms.
   *
   * Targets are resolved from the CONNECTED channel list, so a platform can only
   * be flooded through a channel this tenant actually has — there is no way to
   * type a channel id that does not exist.
   */
  async function submitBulkFlood(data: MessageTestFormData): Promise<boolean> {
    const selectedTypes = data.bulkPlatformTypes || []
    const targets = selectedTypes
      .map((platformType) => {
        const channel = connectedPlatformTargets.find(
          (item) => item.platformType === platformType
        )
        if (!channel) return null

        return {
          platformType,
          dbDriverId: channel.dbDriverId,
          ...(channel.channelId && { channelId: channel.channelId })
        }
      })
      .filter(
        (target): target is RandomMessageBulkPayload["targets"][number] =>
          !!target
      )

    if (targets.length === 0) {
      showErrorToast("Select at least one connected platform")
      return false
    }

    const customMessages = data.messages
      .split("\n")
      .map((message) => message.trim())
      .filter((message) => message.length > 0)

    const payload: RandomMessagePayload = {
      customerId: data.customerId,
      bulk: {
        targets,
        conversationCount: Number(data.bulkConversationCount) || 1,
        messagesPerConversation: Number(data.bulkMessagesPerConversation) || 1,
        ...(data.bulkDelayMs !== undefined &&
          data.bulkDelayMs !== null && { delayMs: Number(data.bulkDelayMs) }),
        ...(data.bulkContactPrefix?.trim() && {
          contactPrefix: data.bulkContactPrefix.trim()
        }),
        ...(customMessages.length > 0 && { messages: customMessages })
      }
    }

    const {
      isSuccess,
      data: resultMessage,
      errorMessage
    } = await createRandomMessageMutation(payload, data.apiUrl)

    if (!isSuccess) {
      const message = String(
        errorMessage || "Failed to start the message flood"
      )
      setLastResult({ success: false, message })
      showErrorToast(message)
      return false
    }

    const message = String(resultMessage || "Message flood started")
    setLastResult({ success: true, message })
    showSuccessToast(message)
    return true
  }

  /**
   * Both send buttons run the same submit; the only difference is whether the
   * existing contact is wiped first. Bulk mode never deletes — it has no single
   * contact — so it short-circuits before that step.
   */
  function submitWith(mode: SendMode) {
    return handleSubmit(async (data: MessageTestFormData) => {
      setSendMode(mode)
      setLastResult(null)

      if (data.bulkEnabled) {
        await submitBulkFlood(data)
        setSendMode(null)
        return
      }

      const messagesArray = data.messages
        .split("\n")
        .map((msg) => msg.trim())
        .filter((msg) => msg.length > 0)
      const mediaItems = buildMediaItems(data)
      const hasMedia = mediaItems.some((media) => media.path.length > 0)

      if (messagesArray.length === 0 && !hasMedia) {
        showErrorToast("Please provide at least one message or media")
        setSendMode(null)
        return
      }

      const socialMessageType = isSocialPlatform(data.platformType)
        ? data.messageType || MessageType.Comment
        : undefined
      const isCommentPlatform = socialMessageType === MessageType.Comment
      const isEmailPlatform = data.platformType === PlatformType.Email

      if (mode === "freshContact") {
        const deleted = await deleteExistingContactIfAny(data)
        if (deleted) {
          showSuccessToast("Existing contact deleted, sending fresh message...")
        }
      }

      const payload: RandomMessagePayload = {
        customerId: data.customerId,
        dbDriverId: data.dbDriverId,
        messages: messagesArray.length > 0 ? messagesArray : [""],
        platformType: data.platformType,
        ...(socialMessageType && { messageType: socialMessageType }),
        channelId: data.channelId,
        ...(data.channelPersonId && { channelPersonId: data.channelPersonId }),
        ...(data.name && { name: data.name }),
        ...(data.phone && { phone: data.phone }),
        ...(data.delayInSeconds !== undefined &&
          data.delayInSeconds !== null && {
            delayInSeconds: data.delayInSeconds
          }),
        ...(isCommentPlatform && {
          commentData: {
            postDescription: data.postDescription,
            postImageUrl: data.postImageUrl,
            postUrl: data.postUrl
          }
        }),
        ...(isQuestionPlatform(data.platformType) && {
          questionData: {
            productImageUrl: data.productImageUrl,
            productLink: data.productLink,
            productMainId: data.productMainId,
            productName: data.productName,
            sku: data.sku
          }
        }),
        ...(isEmailPlatform && data.subject && { subject: data.subject }),
        isTicket: data.isTicket,
        ...(data.isTicket &&
          data.ticketType && { ticketType: data.ticketType }),
        ...(data.isTicket &&
          data.ticketStateId && { ticketStateId: data.ticketStateId }),
        ...(data.isTicket &&
          data.ticketAssigneeId && { ticketAssigneeId: data.ticketAssigneeId }),
        ...(data.isTicket &&
          data.ticketPriority && { ticketPriority: data.ticketPriority }),
        ...(data.isTicket && data.tagIds && { tagIds: [data.tagIds] }),
        ...(hasMedia && { medias: mediaItems })
      }

      const { isSuccess, errorMessage } = await createRandomMessageMutation(
        payload,
        data.apiUrl
      )

      if (!isSuccess) {
        setLastResult({
          success: false,
          message: String(errorMessage || "Failed to create random message")
        })
        showErrorToast(
          String(errorMessage || "Failed to create random message")
        )
        setSendMode(null)
        return
      }

      setLastResult({
        success: true,
        message:
          mode === "freshContact"
            ? "Contact reset and random message created successfully!"
            : "Random message created successfully!"
      })
      showSuccessToast("Random message created successfully!")
      setSendMode(null)
    })
  }

  function handleReset() {
    reset(defaultFormValues())
    replaceMedia([{ ...DEFAULT_MEDIA_ITEM }])
    setLastResult(null)
  }

  const { customerOptions, isLoading: customersLoading } =
    useCustomersQuery(true)
  const { data: channelsData, isLoading: channelsLoading } = useQuery({
    queryKey: ["channels-data-table", customerId],
    queryFn: async () => {
      const response = await channelsGetAllPlatform({ customerId })

      const currentDbDriverId = watch("dbDriverId")
      if (
        currentDbDriverId &&
        response.data?.some((channel) => channel._id === currentDbDriverId)
      ) {
        setValue("dbDriverId", currentDbDriverId)
      }

      return response.data
    },
    enabled: !!customerId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  /**
   * Platform types this tenant actually has a live channel for.
   *
   * The bulk picker must never offer a platform with no connected channel: the
   * flood is delivered through a real `Platforms._id`, so an unconnected type
   * would just fail per message. One entry per type — the first active channel of
   * that type is the one the traffic arrives on.
   */
  const connectedPlatformTargets = useMemo(() => {
    const byType = new Map<
      PlatformType,
      {
        platformType: PlatformType
        dbDriverId: string
        channelId?: string
        name: string
      }
    >()

    ;(channelsData || [])
      .filter(
        (channel) => channel?.status && channel?.channelType && channel?._id
      )
      .forEach((channel) => {
        const platformType = channel.channelType as PlatformType
        if (byType.has(platformType)) return

        byType.set(platformType, {
          platformType,
          dbDriverId: String(channel._id),
          channelId: channel.channelId ? String(channel.channelId) : undefined,
          name: channel.name || platformType
        })
      })

    return Array.from(byType.values())
  }, [channelsData])

  const { data: ticketTypes } = useQuery({
    queryKey: ["message-test-ticket-types", customerId, apiUrl],
    queryFn: async () => {
      if (!customerId) return []
      const response = await query<{
        getTicketTypes?: TicketTypeListResponse
      }>({
        query: GET_TICKET_TYPES,
        variables: {
          input: {
            customer: customerId,
            status: TicketStatusType.Active
          }
        },
        context: apiUrl ? { uri: `${apiUrl}/graphql` } : undefined
      })
      return response.data?.getTicketTypes?.data || []
    },
    enabled: !!customerId && !!isTicketEnabled
  })

  const { data: ticketStates } = useQuery({
    queryKey: ["message-test-ticket-states", customerId, apiUrl],
    queryFn: async () => {
      if (!customerId) return []
      const response = await query<{
        getTicketStates?: TicketStateListResponse
      }>({
        query: GET_TICKET_STATES,
        variables: {
          input: {
            customer: customerId
          }
        },
        context: apiUrl ? { uri: `${apiUrl}/graphql` } : undefined
      })
      return response.data?.getTicketStates?.data || []
    },
    enabled: !!customerId && !!isTicketEnabled
  })

  const { data: ticketTags } = useQuery({
    queryKey: ["message-test-ticket-tags", customerId, apiUrl],
    queryFn: async () => {
      if (!customerId) return []
      const response = await query<{
        getTagsList?: OTagsAllResponse
      }>({
        query: GET_TICKET_TAGS,
        variables: {
          input: {
            customerId,
            pageNumber: 0,
            pageSize: 200,
            type: TagType.Ticket
          }
        },
        context: apiUrl ? { uri: `${apiUrl}/graphql` } : undefined
      })
      return response.data?.getTagsList?.data?.docs || []
    },
    enabled: !!customerId && !!isTicketEnabled
  })

  const { data: customerPersons } = useQuery({
    queryKey: ["message-test-customer-persons", customerId, apiUrl],
    queryFn: async () => {
      if (!customerId) return []
      const response = await query<
        {
          customerPersons?: CustomerPersonsListResponse
        },
        QueryCustomerPersonsArgs
      >({
        query: customerPersonsQuery,
        variables: {
          customerId,
          pageSize: 200,
          page: 1,
          excludeArchived: true,
          excludeAi: true,
          notSelectBot: true
        },
        context: apiUrl ? { uri: `${apiUrl}/graphql` } : undefined
      })
      return response.data?.customerPersons?.data?.docs || []
    },
    enabled: !!customerId && !!isTicketEnabled
  })

  useEffect(() => {
    const currentDbDriverId = watch("dbDriverId")
    if (currentDbDriverId && channelsData && !channelsLoading) {
      const channelExists = channelsData.some(
        (channel) => channel._id === currentDbDriverId
      )
      if (channelExists) {
        // Force setValue to trigger re-render
        setValue("dbDriverId", currentDbDriverId, { shouldValidate: true })
      }
    }
  }, [channelsData, channelsLoading, dbDriverId, setValue])

  useEffect(() => {
    if (dbDriverId && channelsData) {
      const selectedChannel = channelsData.find(
        (item) => item._id === dbDriverId
      )
      if (selectedChannel?.channelType) {
        setValue("platformType", selectedChannel.channelType as PlatformType)
      }
    }
  }, [dbDriverId, channelsData, setValue])

  useEffect(() => {
    if (platformType) {
      if (isCommentSelected) {
        setValue("postDescription", "5 numaralı ürünümüz şubemizde'")
        setValue(
          "postImageUrl",
          "https://qpien-dev.s3.us-east-2.amazonaws.com/urlInfo/instagram/17870534450718480-6a604c828be5a11684412fc3"
        )
        setValue(
          "postUrl",
          "https://www.instagram.com/reel/Ch9_dkxD54F/?utm_source=ig_web_copy_link"
        )
        setValue("productImageUrl", "")
        setValue("productLink", "")
        setValue("productMainId", "")
        setValue("productName", "")
        setValue("sku", "")
      } else if (isQuestionPlatform(platformType)) {
        setValue(
          "productImageUrl",
          "https://productimages.hepsiburada.net/s/777/1100/110000711893880.jpg"
        )
        setValue("productLink", "https://hepsiburada.com/a-p-HBCV00006JH95Q")
        setValue("productMainId", "476311718")
        setValue(
          "productName",
          "Porlistan Amara Çift Kapak Emaye Çaydanlık Takımı, Desenli Emaye Çaydanlık Seti"
        )
        setValue("sku", "HBCV00006JH95Q")
        setValue("postDescription", "")
        setValue("postImageUrl", "")
        setValue("postUrl", "")
      } else {
        setValue("postDescription", "")
        setValue("postImageUrl", "")
        setValue("postUrl", "")
        setValue("productImageUrl", "")
        setValue("productLink", "")
        setValue("productMainId", "")
        setValue("productName", "")
        setValue("sku", "")
      }
    }
  }, [platformType, isCommentSelected, setValue])

  const bulkTotalMessages =
    (bulkConversationCount || 0) * (bulkMessagesPerConversation || 0)

  return (
    <Box sx={{ p: 2, maxWidth: 1180, margin: "0 auto" }}>
      <Paper
        variant="outlined"
        sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        {/* Header bar */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper"
          }}
        >
          <ScienceIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={700}>
            Random Message Test
          </Typography>
          {bulkEnabled && (
            <Chip
              size="small"
              color="primary"
              icon={<BoltIcon fontSize="small" />}
              label="BULK"
            />
          )}
          {!bulkEnabled && platformType && (
            <Chip
              size="small"
              variant="outlined"
              icon={<span>{platformIcons[platformType] || "📱"}</span>}
              label={platformType}
            />
          )}
          {!bulkEnabled && isSocial && (
            <Chip
              size="small"
              variant="outlined"
              label={isCommentSelected ? "comment" : "direct message"}
            />
          )}
          {!bulkEnabled && isTicketEnabled && (
            <Chip size="small" color="warning" label="TICKET" />
          )}

          <Box flex={1} />

          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<RestartAltIcon fontSize="small" />}
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
        </Stack>

        <Box sx={{ p: 1.5 }}>
          {lastResult && (
            <Alert
              severity={lastResult.success ? "success" : "error"}
              sx={{ mb: 1.5 }}
              onClose={() => setLastResult(null)}
            >
              {lastResult.message}
            </Alert>
          )}

          {/* Templates */}
          <Section
            title={`ŞABLONLAR (${allTemplates.length})`}
            hint="Bir şablona tıklayınca form o senaryoyla doldurulur."
            action={
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowSaveTemplateDialog(true)}
                disabled={!isValid || loading}
              >
                💾 Save as Template
              </Button>
            }
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {allTemplates.map((template) => (
                <Tooltip key={template.id} title="Click to apply template">
                  <Chip
                    size="small"
                    icon={<span>{template.icon}</span>}
                    label={template.name}
                    onClick={() => applyTemplate(template)}
                    onDelete={
                      template.isCustom
                        ? () => deleteTemplate(template.id)
                        : undefined
                    }
                    color={template.isCustom ? "secondary" : "primary"}
                    variant={template.isCustom ? "outlined" : "filled"}
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Section>

          <Dialog
            open={showSaveTemplateDialog}
            onClose={() => setShowSaveTemplateDialog(false)}
          >
            <DialogTitle>Save as Template</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Template Name"
                fullWidth
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    saveAsTemplate()
                  }
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowSaveTemplateDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveAsTemplate}
                variant="contained"
                disabled={!templateName.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Two column workbench */}
          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1fr) minmax(0, 1fr)"
              },
              gap: 1.5,
              alignItems: "start"
            }}
          >
            {/* Left column — where the message lands */}
            <Stack gap={1.5}>
              <Section title="HEDEF">
                <TextInput
                  label="API URL"
                  disabled={loading}
                  name="apiUrl"
                  placeholder="Select API URL"
                  control={control}
                  type="select"
                  selectOptions={API_URLS}
                  required
                />
                <Autocomplete<{ id: string; label: string }>
                  options={customerOptions}
                  disabled={customersLoading || loading}
                  value={
                    customerOptions.find((o) => o.id === customerId) ?? null
                  }
                  onChange={(_, option) => {
                    setValue("customerId", option?.id ?? "")
                    setValue("dbDriverId", "")
                  }}
                  getOptionLabel={(o) => `${o.label} (${o.id})`}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate("customer")}
                      placeholder="Müşteri adı veya ID ile arayın…"
                    />
                  )}
                  noOptionsText="Müşteri bulunamadı"
                  loadingText="Yükleniyor…"
                  loading={customersLoading}
                />
                {!bulkEnabled && (
                  <>
                    <TextInput<MessageTestFormData>
                      key={`${customerId}-${dbDriverId || "empty"}`}
                      type="select"
                      label="DB Driver ID"
                      name="dbDriverId"
                      placeholder="Select DB Driver"
                      control={control}
                      groupBy={true}
                      selectOptions={
                        channelsData
                          ?.filter((item) => item.status)
                          ?.map((item) => ({
                            value: item._id || "",
                            label: `${item.name} (${item._id})`,
                            icon: platformIcons[item.channelType || ""] || "📱",
                            group: item.channelType || "Other"
                          })) || []
                      }
                      disabled={channelsLoading || !customerId}
                      required
                    />
                    <TextInput<MessageTestFormData>
                      label="Is Ticket?"
                      disabled={loading}
                      name="isTicket"
                      placeholder="Is this a ticket?"
                      control={control}
                      type="select"
                      selectOptions={[
                        { label: "False", value: false },
                        { label: "True", value: true }
                      ]}
                      required
                    />
                    <TextInput<MessageTestFormData>
                      label="Channel ID"
                      disabled={loading}
                      name="channelId"
                      placeholder="Enter channel ID"
                      control={control}
                      required
                    />
                  </>
                )}
              </Section>

              {/* Bulk flood */}
              <Section
                title="⚡ TOPLU SOHBET AKIŞI (YÜK TESTİ)"
                highlighted={!!bulkEnabled}
                action={
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!bulkEnabled}
                        disabled={loading}
                        onChange={(e) =>
                          setValue("bulkEnabled", e.target.checked, {
                            shouldValidate: true
                          })
                        }
                      />
                    }
                    label={<Typography variant="caption">Aç</Typography>}
                  />
                }
                hint="Seçilen platformlara dağıtılmış sahte sohbetlere, tur tur mesaj gönderir. Bu mod açıkken tekil mesaj alanları kullanılmaz (Messages alanı doldurulursa mesaj metni olarak kullanılır)."
              >
                {bulkEnabled && (
                  <>
                    {!customerId && (
                      <Alert severity="info">
                        Önce müşteri seçin — platform listesi bağlı kanallardan
                        gelir.
                      </Alert>
                    )}
                    {!!customerId &&
                      !channelsLoading &&
                      connectedPlatformTargets.length === 0 && (
                        <Alert severity="warning">
                          Bu müşteride bağlı ve aktif kanal yok.
                        </Alert>
                      )}
                    <Autocomplete<
                      { platformType: PlatformType; name: string },
                      true
                    >
                      multiple
                      options={connectedPlatformTargets}
                      disabled={loading || channelsLoading || !customerId}
                      value={connectedPlatformTargets.filter((option) =>
                        (bulkPlatformTypes || []).includes(option.platformType)
                      )}
                      onChange={(_, options) =>
                        setValue(
                          "bulkPlatformTypes",
                          options
                            .slice(0, BULK_LIMITS.MAX_PLATFORMS)
                            .map((option) => option.platformType),
                          { shouldValidate: true }
                        )
                      }
                      getOptionDisabled={() =>
                        (bulkPlatformTypes || []).length >=
                        BULK_LIMITS.MAX_PLATFORMS
                      }
                      getOptionLabel={(option) =>
                        `${platformIcons[option.platformType] || "📱"} ${option.platformType} — ${option.name}`
                      }
                      isOptionEqualToValue={(a, b) =>
                        a.platformType === b.platformType
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`Platformlar (en fazla ${BULK_LIMITS.MAX_PLATFORMS})`}
                          placeholder="Bağlı kanallardan seçin"
                        />
                      )}
                      noOptionsText="Bağlı kanal bulunamadı"
                      loading={channelsLoading}
                    />
                    <TextInput<MessageTestFormData>
                      label="Sohbet sayısı"
                      disabled={loading}
                      name="bulkConversationCount"
                      type="number"
                      placeholder="10"
                      control={control}
                      helperText={`Kaç ayrı sohbet oluşacak (en fazla ${BULK_LIMITS.MAX_CONVERSATIONS}). Platformlara sırayla dağıtılır.`}
                    />
                    <TextInput<MessageTestFormData>
                      label="Sohbet başına mesaj sayısı"
                      disabled={loading}
                      name="bulkMessagesPerConversation"
                      type="number"
                      placeholder="5"
                      control={control}
                      helperText={`Her sohbete kaç mesaj düşecek (en fazla ${BULK_LIMITS.MAX_MESSAGES_PER_CONVERSATION}).`}
                    />
                    <TextInput<MessageTestFormData>
                      label="Mesajlar arası bekleme (ms)"
                      disabled={loading}
                      name="bulkDelayMs"
                      type="number"
                      placeholder="300"
                      control={control}
                      helperText="Akışı izlenebilir tutar. Boş bırakılırsa 300ms."
                    />
                    <TextInput<MessageTestFormData>
                      label="Kişi ön eki"
                      disabled={loading}
                      name="bulkContactPrefix"
                      placeholder="qpien-load"
                      control={control}
                      helperText="Aynı ön ek AYNI sohbetleri tekrar besler; değiştirirsen yeni kişiler oluşur."
                    />
                    <Alert
                      severity={
                        bulkTotalMessages > BULK_LIMITS.MAX_TOTAL_MESSAGES
                          ? "warning"
                          : "info"
                      }
                    >
                      Toplam <b>{bulkTotalMessages}</b> mesaj gönderilecek.
                      Sunucu tarafı en fazla {BULK_LIMITS.MAX_TOTAL_MESSAGES}{" "}
                      mesajla sınırlar ve akış arka planda çalışır.
                    </Alert>
                  </>
                )}
              </Section>

              {!bulkEnabled && (
                <Section
                  title="KİŞİ"
                  hint="Gönderim öncesi kişinin silinip silinmeyeceğini alttaki iki butondan seçersin."
                >
                  <TextInput<MessageTestFormData>
                    label="Channel Person ID"
                    disabled={loading}
                    name="channelPersonId"
                    placeholder="Enter channel person ID (optional)"
                    control={control}
                  />
                  <TextInput<MessageTestFormData>
                    label="Name"
                    disabled={loading}
                    name="name"
                    placeholder="Enter name (optional)"
                    control={control}
                  />
                  <TextInput<MessageTestFormData>
                    label="Phone"
                    disabled={loading}
                    name="phone"
                    placeholder="Enter phone number (optional)"
                    control={control}
                  />
                  <TextInput<MessageTestFormData>
                    label="Delay in Seconds"
                    disabled={loading}
                    name="delayInSeconds"
                    type="number"
                    placeholder="Enter delay in seconds (optional)"
                    control={control}
                  />
                </Section>
              )}

              {!bulkEnabled && isTicketEnabled && (
                <Section title="🎫 TICKET AYARLARI (OPSİYONEL)">
                  <TextInput<MessageTestFormData>
                    label="Ticket Type ID"
                    disabled={loading}
                    name="ticketType"
                    placeholder="Select ticket type (optional)"
                    control={control}
                    type="select"
                    selectOptions={[
                      { label: "Use Channel Default", value: "" },
                      ...((ticketTypes || []).map((item) => ({
                        label: `${item?.name || "-"} (${item?._id || "-"})`,
                        value: item?._id || ""
                      })) || [])
                    ]}
                  />

                  <TextInput<MessageTestFormData>
                    label="Ticket State ID"
                    disabled={loading}
                    name="ticketStateId"
                    placeholder="Select ticket state (optional)"
                    control={control}
                    type="select"
                    selectOptions={[
                      { label: "Use Open/Default", value: "" },
                      ...((ticketStates || []).map((item) => ({
                        label: `${item?.name || "-"} [${item?.category || "-"}] (${item?._id || "-"})`,
                        value: item?._id || ""
                      })) || [])
                    ]}
                  />

                  <TextInput<MessageTestFormData>
                    label="Ticket Assignee ID"
                    disabled={loading}
                    name="ticketAssigneeId"
                    placeholder="Select assignee (optional)"
                    control={control}
                    type="select"
                    selectOptions={[
                      { label: "Unassigned", value: "" },
                      ...((customerPersons || []).map((item) => ({
                        label: `${item?.firstName || ""} ${item?.lastName || ""} (${item?._id || "-"})`,
                        value: item?._id || ""
                      })) || [])
                    ]}
                  />

                  <TextInput<MessageTestFormData>
                    label="Ticket Priority"
                    disabled={loading}
                    name="ticketPriority"
                    placeholder="Select ticket priority"
                    control={control}
                    type="select"
                    selectOptions={[
                      { label: "LOW", value: TicketPriority.Low },
                      { label: "NORMAL", value: TicketPriority.Normal },
                      { label: "HIGH", value: TicketPriority.High },
                      { label: "URGENT", value: TicketPriority.Urgent }
                    ]}
                  />

                  <TextInput<MessageTestFormData>
                    label="Ticket Tag"
                    disabled={loading}
                    name="tagIds"
                    placeholder="Select ticket tag (optional)"
                    control={control}
                    type="select"
                    selectOptions={[
                      { label: "No Tag", value: "" },
                      ...((ticketTags || []).map((item) => ({
                        label: `${item?.name || "-"} (${item?._id || "-"})`,
                        value: item?._id || ""
                      })) || [])
                    ]}
                  />
                </Section>
              )}
            </Stack>

            {/* Right column — what gets sent */}
            <Stack gap={1.5}>
              <Section
                title="MESAJ"
                hint={
                  bulkEnabled
                    ? "Her satır bir mesaj metni; boş bırakılırsa rastgele metinler üretilir."
                    : "Her satır ayrı bir mesaj olarak gönderilir."
                }
              >
                {!bulkEnabled && isSocial && (
                  <TextInput<MessageTestFormData>
                    label="Message Type"
                    disabled={loading}
                    name="messageType"
                    placeholder="Select message type"
                    control={control}
                    type="select"
                    selectOptions={[
                      {
                        label: "💬 Comment — post yorumu olarak gelir",
                        value: MessageType.Comment
                      },
                      {
                        label: "✉️ Direct Message — normal DM olarak gelir",
                        value: MessageType.TextMessage
                      }
                    ]}
                  />
                )}
                <TextInput<MessageTestFormData>
                  label="Messages"
                  disabled={loading}
                  name="messages"
                  placeholder="Enter messages (one per line)"
                  control={control}
                  multiline
                  rows={6}
                  helperText="Enter one or more messages, separated by new lines. Leave empty if you only want to send media."
                />
                {!bulkEnabled && platformType === PlatformType.Email && (
                  <TextInput<MessageTestFormData>
                    label="Email Subject"
                    disabled={loading}
                    name="subject"
                    placeholder="Enter email subject (optional)"
                    control={control}
                  />
                )}
              </Section>

              {!bulkEnabled && (
                <Section
                  title={`🖼️ MEDYA (${mediaFields.length})`}
                  hint="Üretilen her mesaj eklenen tüm medyaları içerir."
                  action={
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={addMediaRow}
                      disabled={loading}
                    >
                      Add media
                    </Button>
                  }
                >
                  {mediaFields.map((field, index) => (
                    <Paper
                      key={field.id}
                      variant="outlined"
                      sx={{ p: 1.25, bgcolor: "action.hover" }}
                    >
                      <Stack gap={1.5}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={2}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            color="text.secondary"
                          >
                            MEDIA {index + 1}
                          </Typography>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            onClick={() => deleteMediaRow(index)}
                            disabled={loading}
                          >
                            Remove
                          </Button>
                        </Stack>

                        <TextInput<MessageTestFormData>
                          label={`Media URL ${index + 1}`}
                          disabled={loading}
                          name={`medias.${index}.path`}
                          placeholder="https://... (image/video URL to attach)"
                          control={control}
                        />

                        <TextInput<MessageTestFormData>
                          label={`Media Type ${index + 1}`}
                          disabled={loading}
                          name={`medias.${index}.type`}
                          placeholder="Media MIME type"
                          control={control}
                          type="select"
                          selectOptions={[
                            { label: "image/jpeg", value: "image/jpeg" },
                            { label: "image/png", value: "image/png" },
                            { label: "image/gif", value: "image/gif" },
                            { label: "image/webp", value: "image/webp" },
                            { label: "video/mp4", value: "video/mp4" },
                            { label: "audio/mpeg", value: "audio/mpeg" },
                            {
                              label: "application/pdf",
                              value: "application/pdf"
                            }
                          ]}
                        />

                        <TextInput<MessageTestFormData>
                          label={`Media Name ${index + 1} (optional)`}
                          disabled={loading}
                          name={`medias.${index}.name`}
                          placeholder="Optional display name"
                          control={control}
                        />
                      </Stack>
                    </Paper>
                  ))}
                </Section>
              )}

              {/* Comment Data Fields - only when the social message is a post comment */}
              {!bulkEnabled && isCommentSelected && (
                <Section title="📱 COMMENT DATA (SOCIAL MEDIA)">
                  <TextInput<MessageTestFormData>
                    label="Post Description"
                    disabled={loading}
                    name="postDescription"
                    placeholder="Enter post description"
                    control={control}
                    multiline
                    rows={2}
                  />

                  <TextInput<MessageTestFormData>
                    label="Post Image URL"
                    disabled={loading}
                    name="postImageUrl"
                    placeholder="Enter post image URL"
                    control={control}
                  />

                  <TextInput<MessageTestFormData>
                    label="Post URL"
                    disabled={loading}
                    name="postUrl"
                    placeholder="Enter post URL"
                    control={control}
                  />
                </Section>
              )}

              {/* Question Data Fields - for e-commerce platforms */}
              {!bulkEnabled && isQuestionPlatform(platformType) && (
                <Section title="🛍️ QUESTION DATA (E-COMMERCE)">
                  <TextInput<MessageTestFormData>
                    label="Product Name"
                    disabled={loading}
                    name="productName"
                    placeholder="Enter product name"
                    control={control}
                  />

                  <TextInput<MessageTestFormData>
                    label="Product Image URL"
                    disabled={loading}
                    name="productImageUrl"
                    placeholder="Enter product image URL"
                    control={control}
                  />

                  <TextInput<MessageTestFormData>
                    label="Product Link"
                    disabled={loading}
                    name="productLink"
                    placeholder="Enter product link"
                    control={control}
                  />

                  <TextInput<MessageTestFormData>
                    label="Product Main ID"
                    disabled={loading}
                    name="productMainId"
                    placeholder="Enter product main ID"
                    control={control}
                  />

                  <TextInput<MessageTestFormData>
                    label="SKU"
                    disabled={loading}
                    name="sku"
                    placeholder="Enter SKU"
                    control={control}
                  />
                </Section>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Action bar */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            px: 1.5,
            py: 1.25,
            borderTop: 1,
            borderColor: "divider",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            bgcolor: "background.paper"
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {bulkEnabled
              ? `Toplu akış: ${bulkTotalMessages} mesaj arka planda gönderilecek.`
              : "Mevcut kişiyi koruyarak mı yoksa silip sıfırdan mı göndereceğini seç."}
          </Typography>

          <Box flex={1} />

          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={
              bulkEnabled ? (
                <BoltIcon fontSize="small" />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
            loading={sendMode === "keepContact"}
            disabled={!isValid || loading}
            onClick={() => void submitWith("keepContact")()}
          >
            {bulkEnabled ? "Toplu Akışı Başlat" : "Kişiyi Silmeden Gönder"}
          </LoadingButton>

          {!bulkEnabled && (
            <Tooltip
              title={
                canDeleteContact
                  ? "Bu kişiyi siler, sonra mesajı sıfırdan bir konuşma olarak gönderir"
                  : "Silme için Customer, Channel ID ve Channel Person ID dolu olmalı"
              }
            >
              <span>
                <LoadingButton
                  variant="contained"
                  color="error"
                  startIcon={<PersonRemoveIcon fontSize="small" />}
                  loading={sendMode === "freshContact"}
                  disabled={!isValid || loading || !canDeleteContact}
                  onClick={() => void submitWith("freshContact")()}
                >
                  Kişiyi Silerek Gönder
                </LoadingButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Paper>
    </Box>
  )
}
